import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, Renderer2, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { BooleanConverter, NumberConverter } from '../../../inputConverter';
import { IFilterSetting, ISortSetting } from '../../../types';
import { BaseComponent } from '../../base.component';

import { ITableColumnSettings, TableCellWrapMode, TableColumnSortMode } from './table-types';

/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-table-ng',
    templateUrl: './table.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './table.component.scss']
})
export class TableComponent extends BaseComponent implements AfterViewInit {
    private _canResizeColumns: boolean = false;
    private _canSelectRows: boolean = false;
    private _columnSettings: ITableColumnSettings[] = [];
    private _columnSortMode: TableColumnSortMode = TableColumnSortMode.None;
    private _data: any[] = null;
    private _hasRowDetails: boolean = false;
    private _isChildTable: boolean = false;
    private _isColumnSizeFixed: boolean = false;
    private _isFilterEnabled: boolean = false;
    private _isLoading: boolean = true;
    private _pageSize: number = 10;
    private _selectedRowIds: number[] = [];
    private _showColumnHeaders: boolean = false;
    private _showFooter: boolean = false;
    private _tableCellWrapMode: TableCellWrapMode = TableCellWrapMode.Ellipsis;
    private _tableHeight: string = '100%';

    columnResizeIndex: number;
    columnResizeStart: number;
    defaultSort: ISortSetting = {
        descending: true,
        field: 'id'
    };
    filterDebounce: number = 500;
    filteredData: any[] = [];
    filterSettings: IFilterSetting[] = [];
    filterSubject = new Subject<string>();
    initialWidth!: number;
    isColumnResizing = false;
    newWidth!: number;
    numPagesLoaded: number = 1;
    scrollToBottomDistance = 65;
    sortSettings: ISortSetting[] = [];
    tableScrollPosition: number = 0;
    updateTime: Date = new Date();

    @ViewChild('table') table: ElementRef<HTMLElement>;
    @ViewChild('tableContainer') tableContainer: ElementRef<HTMLElement>;

    @Input() rowDetailsTemplate: TemplateRef<any> = null;
    @Input() tableFooterTemplate: TemplateRef<any> = null;

    @Input()
    set canResizeColumns(value: boolean) {
        this._canResizeColumns = BooleanConverter(value);
    }
    get canResizeColumns(): boolean {
        return this._canResizeColumns;
    }

    @Input()
    set canSelectRows(value: boolean) {
        this._canSelectRows = BooleanConverter(value);
    }
    get canSelectRows(): boolean {
        return this._canSelectRows;
    }

    get columnCount() {
        return this.columnSettings.length + Number(this.canSelectRows) + Number(this.rowDetailsTemplate != null);
    }

    @Input()
    set columnSettings(value: ITableColumnSettings[]) {
        this._columnSettings = typeof value === 'string' ? JSON.parse(value) : value;

        this.filterSettings.forEach((filterSetting) => {
            const column = this._columnSettings.find(columnSetting => filterSetting.field === columnSetting.propertyName);

            if (column) {
                column.filter = filterSetting.value;
            }
        });
    }
    get columnSettings(): ITableColumnSettings[] {
        return this._columnSettings;
    }

    @Input()
    set columnSortMode(value: TableColumnSortMode) {
        this._columnSortMode = value;
    }
    get columnSortMode(): TableColumnSortMode {
        return this._columnSortMode;
    }

    @Input()
    set data(value: any[]) {
        const initTable = this._data == null && value != null;
        this._data = typeof value === 'string' ? JSON.parse(value) : value;

        if (initTable) {
            this.filteredData = [...this.data];
            this.applyFilter(true);
            this.applySort();
            this.toggleLoading(false);
        }
    }
    get data(): any[] {
        return this._data;
    }

    @Input()
    set dataIDPropertyName(value: string) {
        this.defaultSort.field = value;
    }
    get dataIDPropertyName(): string {
        return this.defaultSort.field;
    }

    @Input()
    set hasRowDetails(value: boolean) {
        this._hasRowDetails = BooleanConverter(value);
    }
    get hasRowDetails(): boolean {
        return this._hasRowDetails;
    }

    @Input()
    set isChildTable(value: boolean) {
        this._isChildTable = BooleanConverter(value);
    }
    get isChildTable(): boolean {
        return this._isChildTable;
    }

    @Input()
    set isColumnSizeFixed(value: boolean) {
        this._isColumnSizeFixed = BooleanConverter(value);
    }
    get isColumnSizeFixed(): boolean {
        return this._isColumnSizeFixed;
    }

    get isLoadingOverlayDisplayed(): boolean {
        return this.columnSettings.length === 0 || this.isLoading || this.filteredData === null;
    }

    @Input()
    set isFilterEnabled(value: boolean) {
        this._isFilterEnabled = BooleanConverter(value);
    }
    get isFilterEnabled(): boolean {
        return this._isFilterEnabled;
    }

    @Input()
    set isLoading(value: boolean) {
        this.toggleLoading(BooleanConverter(value));
    }
    get isLoading(): boolean {
        return this._isLoading;
    }

    @Input()
    set pageSize(value: number) {
        this._pageSize = NumberConverter(value);
    }
    get pageSize(): number {
        return this._pageSize;
    }

    get rowsForDisplay() {
        return this.filteredData.slice(0, this.numPagesLoaded * this.pageSize);
    }

    @Input()
    set selectedRowIds(value: number[]) {
        this._selectedRowIds = value;
    }
    get selectedRowIds(): number[] {
        return this._selectedRowIds;
    }

    @Input()
    set showColumnHeaders(value: boolean) {
        this._showColumnHeaders = BooleanConverter(value);
    }
    get showColumnHeaders(): boolean {
        return this._showColumnHeaders;
    }

    @Input()
    set showFooter(value: boolean) {
        this._showFooter = BooleanConverter(value);
    }
    get showFooter(): boolean {
        return this._showFooter;
    }

    @Input()
    set tableCellWrapMode(value: TableCellWrapMode) {
        this._tableCellWrapMode = value;
    }
    get tableCellWrapMode(): TableCellWrapMode {
        return this._tableCellWrapMode;
    }

    @Input()
    set tableHeight(value: string) {
        this._tableHeight = value;
    }
    get tableHeight(): string {
        return this._tableHeight;
    }

    @Output() public readonly buttonClickEvent = new EventEmitter<{ data: any; event: string }>();
    @Output() public readonly columnResizeEvent = new EventEmitter<{ propertyName: string; width: number }>();
    @Output() public readonly expandCollapseEvent = new EventEmitter<{ data: any }>();
    @Output() public readonly filterEvent = new EventEmitter<{ filteredData: any[]; filterSettings: IFilterSetting[] }>();
    @Output() public readonly rowSelectionEvent = new EventEmitter<{ selectedRowIds: number[] }>();
    @Output() public readonly sortEvent = new EventEmitter<{ sortSettings: ISortSetting[] }>();

    constructor(public _vcr: ViewContainerRef, private readonly cdr: ChangeDetectorRef, private readonly renderer: Renderer2) {
        super(_vcr, true);
        this.registerEvent('buttonClickEvent', this.buttonClickEvent);
        this.registerEvent('columnResizeEvent', this.columnResizeEvent);
        this.registerEvent('expandCollapseEvent', this.expandCollapseEvent);
        this.registerEvent('filterEvent', this.filterEvent);
        this.registerEvent('rowSelectionEvent', this.rowSelectionEvent);
        this.registerEvent('sortEvent', this.sortEvent);
    }

    async ngAfterViewInit() {
        this.tableContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));

        this.filterSubject.pipe(debounceTime(this.filterDebounce)).subscribe(() => {
            this.toggleLoading(true);

            setTimeout(() => {
                this.applyFilter(true);
                this.toggleLoading(false);
            }, 10);
        });
    }

    applyFilter(triggerEvent) {
        this.filteredData = [...this.data];
        const filterSettings = [];

        this.columnSettings.forEach((column) => {
            if (column.canFilter && column.filter && column.filter !== '') {
                this.filteredData = this.filteredData.filter(data => !data[column.propertyName] ? false : data[column.propertyName].toString().toLowerCase().includes(column.filter.toLowerCase()));
                filterSettings.push({
                    field: column.propertyName,
                    operator: 'like',
                    value: column.filter
                });
            }
        });

        if (triggerEvent) {
            this.filterEvent.emit({ filteredData: this.filteredData, filterSettings });
        }
    }

    applySort() {
        const sortSettingsToApply = this.sortSettings.length === 0 ? [this.defaultSort] : this.sortSettings;

        this.filteredData.sort((a, b) => {
            for (const sortSetting of sortSettingsToApply) {
                const aValue = a[sortSetting.field];
                const bValue = b[sortSetting.field];

                if (aValue < bValue) {
                    return !sortSetting.descending ? -1 : 1;
                } else if (aValue > bValue) {
                    return !sortSetting.descending ? 1 : -1;
                }
            }

            return 0;
        });
    }

    onButtonClick(dataItem: any, event: string) {
        this.buttonClickEvent.emit({ data: dataItem, event });
    }

    onFilter() {
        this.filterSubject.next();
    }

    onScroll(_event: any) {
        // When scrolling down and scroll position just above last item and there are more items to load
        if (this.numPagesLoaded * this.pageSize < this.filteredData.length && this.tableScrollPosition < this.tableContainer.nativeElement.scrollTop && this.tableContainer.nativeElement.scrollTop >= this.tableContainer.nativeElement.scrollHeight - this.tableContainer.nativeElement.clientHeight - this.scrollToBottomDistance) {
            this.toggleLoading(true);
            this.numPagesLoaded++;

            setTimeout(() => {
                this.toggleLoading(false);
            }, 500);
        }

        this.tableScrollPosition = this.tableContainer.nativeElement.scrollTop;
    }

    onSortChange(propertyName: string) {
        if (!this.isColumnResizing && this.columnSortMode !== TableColumnSortMode.None && this.columnSettings.find(column => column.propertyName === propertyName).canSort) {
            this.toggleLoading(true);

            setTimeout(() => {
                const sortedProperty = this.sortSettings.find(setting => setting.field === propertyName);

                if (!sortedProperty) {
                    const newSortedProperty = {
                        descending: false,
                        field: propertyName
                    };

                    this.sortSettings.splice(0, this.columnSortMode === TableColumnSortMode.Single ? 1 : 0, newSortedProperty);
                } else {
                    if (sortedProperty.descending) {
                        this.sortSettings.splice(this.sortSettings.indexOf(sortedProperty), 1);
                    } else {
                        sortedProperty.descending = true;
                    }
                }

                this.applySort();

                // Trigger pipe change detection
                this.updateTime = new Date();

                this.sortEvent.emit({ sortSettings: this.sortSettings });
                this.toggleLoading(false);
            }, 10);
        }
    }

    resizeColumn(columnResizer: HTMLSpanElement, propertyName: string, event: MouseEvent) {
        this.isColumnResizing = true;
        const tableHeaderElement = columnResizer.parentElement;
        const tableRow = tableHeaderElement.parentElement;
        const tableRowCells = Array.from(tableRow.children);
        this.columnResizeIndex = tableRowCells.indexOf(tableHeaderElement);

        this.columnResizeStart = event.pageX;
        this.initialWidth = tableHeaderElement.offsetWidth;
        this.newWidth = null;

        const onMouseMove = (mouseMoveEvent: MouseEvent) => {
            if (this.isColumnResizing) {
                this.newWidth = this.initialWidth + mouseMoveEvent.pageX - this.columnResizeStart;
                const tableHeaderTextContainer = tableHeaderElement.querySelectorAll('div')[0];

                if (this.newWidth !== this.initialWidth && this.newWidth >= Number(getComputedStyle(tableHeaderTextContainer).getPropertyValue('min-width').replace('px', ''))) {
                    const margin = Number(getComputedStyle(tableHeaderElement).getPropertyValue('margin-left').replace('px', '')) + Number(getComputedStyle(tableHeaderElement).getPropertyValue('margin-right').replace('px', ''));
                    const padding = Number(getComputedStyle(tableHeaderElement).getPropertyValue('padding-left').replace('px', '')) + Number(getComputedStyle(tableHeaderElement).getPropertyValue('padding-right').replace('px', ''));
                    this.newWidth -= margin + padding;

                    this.renderer.setStyle(tableHeaderElement, 'width', `${this.newWidth}px`);
                    this.renderer.setStyle(tableHeaderTextContainer, 'width', `${this.newWidth}px`);

                    this.table.nativeElement.querySelectorAll('tr').forEach((row) => {
                        const cells = row.querySelectorAll('td');

                        if (cells[this.columnResizeIndex]) {
                            const contentContainer = cells[this.columnResizeIndex].querySelectorAll('div')[0];

                            if (contentContainer !== undefined) {
                                this.renderer.setStyle(contentContainer, 'max-width', `${this.newWidth}px`);
                            }
                        }
                    });
                }
            }
        };

        const onMouseUp = () => {
            // Slight delay to prevent sorting event from executing
            setTimeout(() => { this.isColumnResizing = false; }, 50);

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            this.columnResizeEvent.emit({ propertyName, width: this.newWidth });
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }

    selectAllRows() {
        if (this.selectedRowIds.length) {
            this.selectedRowIds = [];
        } else {
            this.selectedRowIds = this.filteredData.map(data => data[this.dataIDPropertyName]);
        }

        // Trigger pipe change detection
        this.updateTime = new Date();

        this.rowSelectionEvent.emit({ selectedRowIds: this.selectedRowIds });
    }

    selectRow(id: number) {
        const itemIndex = this.selectedRowIds.indexOf(id);

        if (itemIndex < 0) {
            this.selectedRowIds.push(id);
        } else {
            this.selectedRowIds.splice(itemIndex, 1);
        }

        // Trigger pipe change detection
        this.updateTime = new Date();

        this.rowSelectionEvent.emit({ selectedRowIds: this.selectedRowIds });
    }

    toggleLoading(isLoading: boolean) {
        this._isLoading = isLoading;
        this.cdr.detectChanges();
    }

    toggleRowExpandCollapse(dataItem: any) {
        dataItem.isExpanded = !dataItem.isExpanded;
        this.expandCollapseEvent.emit({ data: dataItem });
    }
}
